@if (isOpen() && application(); as app) {
  <!-- BACKDROP OVERLAY -->
  <div
    class="fixed inset-0 bg-neutral-12/40 backdrop-blur-xs z-50 transition-opacity"
    (click)="close()"
    tabindex="-1"
    aria-hidden="true"
  ></div>

  <!-- TIROIR LATÉRAL (SLIDE-OVER) -->
  <div
    class="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white shadow-2xl flex flex-col border-l border-neutral-4 animate-in slide-in-from-right duration-300"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="'Dossier de candidature de ' + agentName()"
  >
    <!-- 1. EN-TÊTE DU TIROIR -->
    <div class="flex items-center justify-between p-5 sm:p-6 border-b border-neutral-4 bg-white shrink-0">
      <div class="flex items-center gap-3.5 min-w-0">
        <!-- Avatar cliquable -->
        <button
          type="button"
          (click)="openAgentProfileModal()"
          class="relative size-12 shrink-0 rounded-xl overflow-hidden bg-indigo-2 text-indigo-11 border border-indigo-4 flex items-center justify-center font-bold text-base shadow-2xs hover:ring-2 hover:ring-indigo-6 hover:scale-105 transition-all cursor-pointer p-0"
          matTooltip="Voir le profil professionnel de l'agent"
        >
          @if (agentAvatarUrl(); as avatar) {
            <img [src]="avatar" [alt]="agentName()" class="h-full w-full object-cover" />
          } @else {
            <span>{{ agentInitials() }}</span>
          }
        </button>

        <!-- Nom, Agence, Carte T -->
        <div class="flex flex-col min-w-0">
          <div class="flex items-center gap-2">
            <button
              type="button"
              (click)="openAgentProfileModal()"
              class="group/name flex items-center gap-1.5 text-left p-0 border-0 bg-transparent cursor-pointer"
              matTooltip="Voir le profil professionnel de l'agent"
            >
              <h2
                class="text-base sm:text-lg font-bold text-neutral-12 group-hover/name:text-primary transition-colors truncate m-0"
              >
                {{ agentName() }}
              </h2>
              <mat-icon
                class="size-4 text-base! text-neutral-8 group-hover/name:text-primary transition-colors shrink-0"
                >open_in_new</mat-icon
              >
            </button>
            @if (isAccepted()) {
              <span
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-jade-2 text-jade-11 border border-jade-5 shrink-0"
              >
                <mat-icon class="size-3 text-[11px]!">handshake</mat-icon>
                <span>Mandataire sélectionné</span>
              </span>
            }
          </div>

          <div class="flex items-center gap-2 mt-0.5 text-xs text-neutral-10 truncate flex-wrap">
            <span class="font-semibold text-neutral-12 flex items-center gap-1">
              <mat-icon class="size-3.5 text-[13px]! text-primary-10">
                {{
                  agentAffiliation().isTitulaire
                    ? 'badge'
                    : agentAffiliation().agencyName
                      ? 'corporate_fare'
                      : 'hub'
                }}
              </mat-icon>
              <span>{{ agentAffiliation().displayBadge }}</span>
            </span>
            <span class="text-neutral-8">•</span>
            <span class="text-2xs text-neutral-9 font-medium">{{ agentAffiliation().typeLabel }}</span>
            <span class="text-neutral-8">•</span>
            <span class="flex items-center gap-1 text-jade-11 font-medium">
              <mat-icon class="size-3.5 text-[13px]! text-jade-10">verified</mat-icon>
              <span>Carte T vérifiée</span>
            </span>
          </div>
        </div>
      </div>

      <!-- Boutons En-tête : Discussion & Fermer -->
      <div class="flex items-center gap-2">
        <button
          mat-icon-button
          (click)="close()"
          type="button"
          aria-label="Fermer le tiroir"
          class="text-neutral-10! hover:text-neutral-12!"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- 2. CORPS DU TIROIR (DÉFILEMENT FLUIDE) -->
    <div class="flex-1 overflow-y-auto p-5 sm:p-7 flex flex-col gap-6 bg-white">
      <!-- BANNIÈRE D'ÉTAT & EXPLICATIONS PROPRIÉTAIRE -->
      @switch (app.status) {
        @case ('pending') {
          <div
            class="flex items-start gap-3 p-4 rounded-xl border border-amber-4 bg-amber-2/40 text-amber-12"
          >
            <mat-icon class="size-5 text-lg! text-amber-11 shrink-0 mt-0.5">schedule</mat-icon>
            <div class="flex flex-col gap-0.5">
              <span class="text-xs font-bold uppercase tracking-wider">Candidature reçue</span>
              <p class="text-xs text-amber-11 m-0 leading-relaxed">
                Cet agent a candidaté sur votre bien avec sa proposition d'honoraires initiale. Vous pouvez
                échanger avec lui par messagerie pour organiser un rendez-vous de visite.
              </p>
            </div>
          </div>
        }
        @case ('visit_invited') {
          <div class="flex items-start gap-3 p-4 rounded-xl border border-sky-4 bg-sky-2/40 text-sky-12">
            <mat-icon class="size-5 text-lg! text-sky-11 shrink-0 mt-0.5">event</mat-icon>
            <div class="flex flex-col gap-0.5">
              <span class="text-xs font-bold uppercase tracking-wider">Visite du bien proposée</span>
              <p class="text-xs text-sky-11 m-0 leading-relaxed">
                Une proposition de rendez-vous est en cours pour permettre à l'agent de visiter votre bien et
                réaliser son avis de valeur.
              </p>
            </div>
          </div>
        }
        @case ('visit_confirmed') {
          <div
            class="flex items-start gap-3 p-4 rounded-xl border border-indigo-4 bg-indigo-2/40 text-indigo-12"
          >
            <mat-icon class="size-5 text-lg! text-indigo-11 shrink-0 mt-0.5">event_available</mat-icon>
            <div class="flex flex-col gap-0.5">
              <span class="text-xs font-bold uppercase tracking-wider">Visite confirmée</span>
              <p class="text-xs text-indigo-11 m-0 leading-relaxed">
                Le rendez-vous de visite est planifié. L'agent se déplacera pour expertiser le logement avant
                de vous formuler son estimation et son offre finale.
              </p>
            </div>
          </div>
        }
        @case ('visited') {
          <div
            class="flex items-start gap-3 p-4 rounded-xl border border-indigo-4 bg-indigo-2/40 text-indigo-12"
          >
            <mat-icon class="size-5 text-lg! text-indigo-11 shrink-0 mt-0.5">check_circle</mat-icon>
            <div class="flex flex-col gap-0.5">
              <span class="text-xs font-bold uppercase tracking-wider">Visite effectuée</span>
              <p class="text-xs text-indigo-11 m-0 leading-relaxed">
                La visite s'est bien déroulée. L'agent prépare actuellement son avis de valeur détaillé et sa
                proposition d'honoraires définitive.
              </p>
            </div>
          </div>
        }
        @case ('offer_submitted') {
          <div
            class="flex items-start gap-3 p-4 rounded-xl border border-purple-4 bg-purple-2/40 text-purple-12"
          >
            <mat-icon class="size-5 text-lg! text-purple-11 shrink-0 mt-0.5">price_check</mat-icon>
            <div class="flex flex-col gap-0.5">
              <span class="text-xs font-bold uppercase tracking-wider"
                >Offre finale & avis de valeur reçus</span
              >
              <p class="text-xs text-purple-11 m-0 leading-relaxed">
                L'agent a finalisé son estimation et fixé ses honoraires définitifs. Vous disposez de tous les
                éléments pour lui confier le mandat de vente !
              </p>
            </div>
          </div>
        }
        @case ('accepted') {
          <div class="flex items-start gap-3 p-4 rounded-xl border border-jade-5 bg-jade-2/50 text-jade-12">
            <mat-icon class="size-5 text-lg! text-jade-11 shrink-0 mt-0.5">handshake</mat-icon>
            <div class="flex flex-col gap-0.5">
              <span class="text-xs font-bold uppercase tracking-wider">Mandat attribué</span>
              <p class="text-xs text-jade-11 m-0 leading-relaxed">
                Vous avez accordé votre confiance à cet agent pour commercialiser votre bien selon les
                conditions convenues.
              </p>
            </div>
          </div>
        }
        @case ('rejected') {
          <div
            class="flex items-start gap-3 p-4 rounded-xl border border-neutral-4 bg-neutral-2 text-neutral-12"
          >
            <mat-icon class="size-5 text-lg! text-neutral-10 shrink-0 mt-0.5">cancel</mat-icon>
            <div class="flex flex-col gap-0.5">
              <span class="text-xs font-bold uppercase tracking-wider">Candidature déclinée</span>
              <p class="text-xs text-neutral-10 m-0 leading-relaxed">
                Cette candidature n'a pas été retenue pour la vente de votre bien.
              </p>
            </div>
          </div>
        }
        @case ('expired') {
          <div class="flex items-start gap-3 p-4 rounded-xl border border-red-4 bg-red-2/40 text-red-12">
            <mat-icon class="size-5 text-lg! text-red-11 shrink-0 mt-0.5">timer_off</mat-icon>
            <div class="flex flex-col gap-0.5">
              <span class="text-xs font-bold uppercase tracking-wider">Délai de réponse dépassé</span>
              <p class="text-xs text-red-11 m-0 leading-relaxed">
                Le délai imparti de 24h a expiré sans réponse. La candidature a été clôturée automatiquement.
              </p>
            </div>
          </div>
        }
        @case ('cancelled') {
          <div class="flex items-start gap-3 p-4 rounded-xl border border-red-5/60 bg-red-2/50 text-red-12">
            <mat-icon class="size-5 text-lg! text-red-11 shrink-0 mt-0.5">cancel</mat-icon>
            <div class="flex flex-col gap-0.5">
              <span class="text-xs font-bold uppercase tracking-wider">Candidature retirée</span>
              <p class="text-xs text-red-11 m-0 leading-relaxed">
                L'agent s'est retiré de sa candidature sur votre bien. Sa place a été automatiquement libérée
                pour d'autres agents.
              </p>
            </div>
          </div>
        }
      }

      <!-- TIMELINE DES ÉTAPES -->
      <div class="rounded-2xl border border-neutral-3 bg-neutral-1/60 p-4 sm:p-5 flex flex-col gap-3">
        <span class="text-xs font-bold uppercase tracking-wider text-neutral-11">
          Avancement de la démarche
        </span>
        <etxe-property-application-timeline
          [status]="app.status"
          [application]="app"
          [appointment]="appointmentDto()"
          [property]="property()"
        />
      </div>

      <!-- BLOC RATTACHEMENT & CARTE PROFESSIONNELLE -->
      <div class="rounded-2xl border border-neutral-3 bg-neutral-1/60 p-4 sm:p-5 flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <span class="text-xs font-bold uppercase tracking-wider text-neutral-11 flex items-center gap-1.5">
            <mat-icon class="size-4 text-base! text-primary-10">verified_user</mat-icon>
            <span>Rattachement professionnel et carte T</span>
          </span>
          <span
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-jade-2 text-jade-11 border border-jade-5"
          >
            <mat-icon class="size-3 text-[11px]!">check_circle</mat-icon>
            <span>Vérifié CCI</span>
          </span>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs pt-1">
          <div class="flex flex-col gap-0.5">
            <span class="text-[10px] font-semibold text-neutral-9 uppercase">Statut d'exercice</span>
            <span class="font-bold text-neutral-12">{{ agentAffiliation().typeLabel }}</span>
          </div>

          @if (agentAffiliation().network) {
            <div class="flex flex-col gap-0.5">
              <span class="text-[10px] font-semibold text-neutral-9 uppercase">Réseau commercial</span>
              <span class="font-bold text-neutral-12">{{ agentAffiliation().network }}</span>
            </div>
          }

          @if (agentAffiliation().agencyName) {
            <div class="flex flex-col gap-0.5">
              <span class="text-[10px] font-semibold text-neutral-9 uppercase"
                >Structure / Agence titulaire</span
              >
              <span class="font-bold text-neutral-12">{{ agentAffiliation().agencyName }}</span>
            </div>
          }

          @if (agentCardNumber()) {
            <div class="flex flex-col gap-0.5">
              <span class="text-[10px] font-semibold text-neutral-9 uppercase"
                >N° de carte T ou d'habilitation</span
              >
              <span class="font-mono text-neutral-11">{{ agentCardNumber() }}</span>
            </div>
          }
        </div>
      </div>

      <!-- PROPOSITION COMMERCIALE & VALORISATION -->
      <etxe-property-application-engagement-display [application]="app" [property]="property()" />
    </div>

    <!-- 3. PIED DE TIROIR : ACTIONS DÉCISIONNELLES DU PROPRIÉTAIRE -->
    <div
      class="p-4 sm:p-6 border-t border-neutral-4 bg-white flex items-center justify-between gap-3 shrink-0"
    >
      <!-- Bouton Refuser -->
      @if (!isClosed()) {
        <button
          mat-stroked-button
          class="py-2.5! px-4! text-xs! font-semibold! text-red-11 hover:bg-red-2! border-red-4! transition-colors"
          (click)="onReject()"
          type="button"
        >
          <span>Décliner</span>
        </button>
      } @else {
        <div></div>
      }

      <!-- Actions Droite -->
      <div class="flex items-center gap-3">
        <button
          mat-stroked-button
          class="py-2.5! px-4! text-xs! font-semibold!"
          (click)="openDiscussion()"
          type="button"
        >
          <mat-icon class="size-4 text-base! mr-1 text-neutral-11">chat</mat-icon>
          <span>Discuter</span>
        </button>

        @if (!isAccepted() && canAccept()) {
          <button
            mat-flat-button
            class="primary py-2.5! px-5! text-xs! font-bold! flex items-center gap-1.5 shadow-xs"
            (click)="onAccept()"
            type="button"
          >
            <mat-icon class="size-4 text-base!">handshake</mat-icon>
            <span>Attribuer le mandat</span>
          </button>
        }
      </div>
    </div>
  </div>
}
